import api from "../../../core/api";
import {
  ChangeMachine,
  DecommissionPreview,
  Machine,
  MachineType,
  MachinesQuery,
  ManufacturerReference,
  OperationType,
  AxisConfiguration,
  ControlSystemBrand,
  ControlSystemModel,
  AutomationLevel,
  ReportProblemRequest,
  ReportProblemResponse,
} from "../models/models";

const getRequestConfig = () => {
  return { baseURL: (window as any).app.config.machineryApiBaseUrl };
}

class CustomerMachineryApi {
  getMachines(query: MachinesQuery): Promise<Record<string, Machine[]>> {
    return api.get<Record<string, Machine[]>>(
      `/v1/customer/machines`, { ...getRequestConfig(), params: query, paramsSerializer: { indexes: null } })
      .then(x => x.data);
  }

  getMachine(id: string): Promise<Machine> {
    return api.get<Machine>(
        `/v1/customer/machines/${id}`, { ...getRequestConfig() })
        .then(x => x.data);
  }

  createMachine(model: ChangeMachine): Promise<string> {
    return api.post<string>(`/v1/customer/machines`, model, getRequestConfig()).then(x => x.data);
  }

  changeMachine(id: string, model: ChangeMachine) {
    return api.put<any>(`/v1/customer/machines/${id}`, model, getRequestConfig());
  }

  deleteMachine(id: string): Promise<any> {
    return api.delete(`/v1/customer/machines/${id}`, getRequestConfig());
  }

  getTypes(): Promise<MachineType[]> {
    return api.get<MachineType[]>(`/v1/customer/machines/types`, getRequestConfig())
      .then(x => x.data);
  }

  getManufacturersById(machineTypeId: string): Promise<ManufacturerReference[]> {
    return api.get<ManufacturerReference[]>(`/v1/customer/machines/types/${machineTypeId}/manufacturers`, getRequestConfig())
      .then(x => x.data);
  }

  getOperationTypes(machineTypeId: string): Promise<OperationType[]> {
    return api.get<OperationType[]>(`/v1/customer/machines/types/${machineTypeId}/operation-types`, getRequestConfig())
      .then(x => x.data);
  }

  getAxisConfigurations(operationTypeId: string): Promise<AxisConfiguration[]> {
    return api.get<AxisConfiguration[]>(`/v1/customer/machines/operation-types/${operationTypeId}/axis-configurations`, getRequestConfig())
      .then(x => x.data);
  }

  getControlSystemBrands(machineTypeId: string): Promise<ControlSystemBrand[]> {
    return api.get<ControlSystemBrand[]>(`/v1/customer/machines/types/${machineTypeId}/control-system-brands`, getRequestConfig())
      .then(x => x.data);
  }

  getControlSystemModels(controlSystemId: string): Promise<ControlSystemModel[]> {
    return api.get<ControlSystemModel[]>(`/v1/customer/machines/control-systems/${controlSystemId}/models`, getRequestConfig())
      .then(x => x.data);
  }

  getAutomationLevels(): Promise<AutomationLevel[]> {
    return api.get<AutomationLevel[]>(`/v1/customer/machines/automation-levels`, getRequestConfig())
      .then(x => x.data);
  }

  // --- Decommission ---

  getDecommissionPreview(id: string): Promise<DecommissionPreview> {
    return api.get<DecommissionPreview>(
      `/v1/customer/machines/${id}/decommission-preview`, getRequestConfig()
    ).then(x => x.data);
  }

  decommissionMachine(id: string, data?: { reason?: string; forceArchiveRfs?: boolean }): Promise<void> {
    return api.delete(`/v1/customer/machines/${id}`, { ...getRequestConfig(), data }).then(() => {});
  }

  restoreMachine(id: string): Promise<void> {
    return api.post(`/v1/customer/machines/${id}/restore`, null, getRequestConfig()).then(() => {});
  }

  getDecommissionedMachines(): Promise<Machine[]> {
    return api.get<Machine[]>(
      `/v1/customer/machines/decommissioned`, getRequestConfig()
    ).then(x => x.data);
  }

  // --- Report Problem ---

  reportProblem(machineId: string, model: ReportProblemRequest): Promise<ReportProblemResponse> {
    return api.post<ReportProblemResponse>(
      `/v1/customer/machines/${machineId}/report-problem`, model, getRequestConfig()
    ).then(x => x.data);
  }
}

export default new CustomerMachineryApi();
